import React from 'react';
import { StyleSheet, Platform, View, Image, Text, TouchableOpacity } from 'react-native';
import { useNavigation, type CompositeNavigationProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MaterialIcons } from '@expo/vector-icons';
import Dimens from 'src/core/utils/Dimens';
import Images from 'src/core/utils/Images';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import type { GalleryStackParamList, RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

type TopicusListNav = CompositeNavigationProp<
	NativeStackNavigationProp<RootStackParamList>,
	NativeStackNavigationProp<GalleryStackParamList>
>;

interface TopicusListItemProps {
	data: ApiPage;
	noBorderBottom?: boolean;
	image?: string;
	title?: string;
	description?: string;
	rootGallery?: boolean;
}

function TopicusListItem({ data, noBorderBottom, image, title, description, rootGallery }: TopicusListItemProps) {
	const navigation = useNavigation<TopicusListNav>();
	const showItem = (item: ApiPage) => {
		if (item.type === 'Gallery') {
			if (rootGallery) {
				navigation.navigate(Constants.screenRootGalleryPage, item);
			} else {
				navigation.navigate(Constants.screenGallery, { data: item });
			}
		} else {
			// Also a page the beheerder left empty: its detail screen says so, where a dead row said nothing.
			navigation.navigate(item.type, { data: item });
		}
	};

	return (
		<TouchableOpacity onPress={() => showItem(data)}>
			<View style={noBorderBottom ? styles.noLine : styles.line}>
				<Image resizeMode="cover" source={image ? { uri: image } : Images.placeholder} style={styles.img} />
				<View style={{ justifyContent: 'center', flex: 1 }}>
					{description?.length ? (
						<View>
							<Text numberOfLines={1} style={styles.heading}>
								{title}
							</Text>
							<View style={{ height: 3 }} />
							<Text numberOfLines={1} style={styles.description}>
								{description}
							</Text>
						</View>
					) : (
						<Text numberOfLines={2} style={[styles.heading, styles.twoLinesHeading]}>
							{title}
						</Text>
					)}
				</View>
				{Platform.OS === 'ios' ? <MaterialIcons name="keyboard-arrow-right" style={styles.arrow} /> : null}
			</View>
		</TouchableOpacity>
	);
}

export default TopicusListItem;

const styles = StyleSheet.create({
	img: {
		height: Platform.OS === 'android' ? 40 : 45,
		width: Platform.OS === 'android' ? 40 : 45,
		borderRadius: Platform.OS === 'android' ? 40 : 3,
	},
	line: {
		flex: 1,
		flexDirection: 'row',
		flexWrap: 'nowrap',
		alignItems: 'center',
		paddingTop: Platform.OS === 'ios' ? Dimens.padding12 : Dimens.padding10,
		paddingBottom: Platform.OS === 'ios' ? Dimens.padding12 : Dimens.padding10,
		marginLeft: Platform.OS === 'ios' ? Dimens.margin20 : Dimens.margin16,
		borderBottomWidth: Platform.OS === 'ios' ? 0.5 : undefined,
		borderColor: Platform.OS === 'ios' ? Colors.overlay15 : undefined,
	},
	noLine: {
		flex: 1,
		flexDirection: 'row',
		flexWrap: 'nowrap',
		alignItems: 'center',
		paddingTop: Platform.OS === 'ios' ? Dimens.padding12 : Dimens.padding10,
		paddingBottom: Platform.OS === 'ios' ? Dimens.padding12 : Dimens.padding10,
		marginLeft: Platform.OS === 'ios' ? Dimens.margin20 : Dimens.margin16,
		borderBottomWidth: Platform.OS === 'ios' ? 0 : undefined,
		borderColor: Platform.OS === 'ios' ? Colors.overlay15 : undefined,
	},
	heading: {
		fontSize: Dimens.fontSize17,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding40,
		lineHeight: Platform.OS === 'ios' ? Dimens.lineHeight23 : 17,
	},
	twoLinesHeading: {
		lineHeight: Platform.OS === 'android' ? Dimens.lineHeight23 : undefined,
		marginTop: Platform.OS === 'android' ? -8 : undefined,
	},
	description: {
		color: Platform.OS === 'android' ? Colors.overlay54 : Colors.greyText,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize14 : Dimens.fontSize12,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratMedium : Fonts.montserratMedium,
		// Android clips the descender (g, j, p) of a single-line custom-font Text without an explicit line height;
		// reserve enough leading so the full glyph stays visible.
		lineHeight: Platform.OS === 'android' ? Dimens.lineHeight20 : undefined,
		paddingLeft: Dimens.padding20,
		paddingRight: Platform.OS === 'android' ? Dimens.padding20 : Dimens.padding40,
	},
	arrow: {
		fontSize: Dimens.fontSize23,
		color: Colors.greyIcon,
		position: 'absolute',
		right: 20,
	},
});
